import { RoleId } from "@/types/user-management";
import { redirect } from "@tanstack/react-router";
import { jwtDecode } from "jwt-decode";
import { z } from "zod/v4";
import { StorageUtility } from "./storage";

const TokenSchema = z.object({
  jti: z.string(),
  iat: z.number(),
  sub: z.string(),
  iss: z.string(),
  userAccountId: z.string(),
  exp: z.number(),
  merchantId: z.string().optional(),
  adminId: z.string().optional(),
  authorities: z.array(z.string()),
});
export type Token = z.infer<typeof TokenSchema>;

/**
 * Check if the token is expired
 * If it fails to match the schema, consider it expired
 */
export const jwtIsExpired = (token: string) => {
  const decoded = jwtDecode(token);
  const parsed = TokenSchema.safeParse(decoded);
  if (!parsed.success) {
    return true;
  }
  const now = new Date().getTime() / 1000;
  if (now > parsed.data.exp) {
    return true;
  }
  return false;
};

/**
 * throw this function to redirect to the sign in page
 *
 * @param preserveUrl - if true, the current url will be preserved in the search params
 * @default true
 */
export function redirectToSignIn(preserveUrl = true) {
  if (!preserveUrl || window.location.pathname === "/") {
    return redirect({
      to: "/auth/signin",
    });
  }

  return redirect({
    to: "/auth/signin",
    search: {
      afterSignInUrl: window.location.pathname + window.location.search,
    },
  });
}

/**
 * Ideally use the effectful version of this function in jwt.ts
 */
export function parseVolumeJwt(token: string): Token {
  const decodedPayload = jwtDecode(token, { header: false });
  const parsed = TokenSchema.parse(decodedPayload);
  return parsed;
}

export const getJwtFromStorage = () => {
  const token = StorageUtility.getItem("v_accessToken_v1");
  if (!token) {
    throw redirectToSignIn();
  }
  return token;
};

export const validateSession = (token: string) => {
  if (jwtIsExpired(token)) {
    throw redirectToSignIn();
  }
};

export const validateUserSession = (tokenContents: Token) => {
  const loggedInUserEmail = StorageUtility.getItem("v_email_v1");

  if (tokenContents.merchantId && !loggedInUserEmail) {
    throw redirectToSignIn();
  }

  if (tokenContents.adminId && !StorageUtility.getItem("v_merchantId_v1")) {
    /**
     * This is likely where there is an ?afterSignInUrl=/balances/overview or any other merchant specific url
     * being visited when logged in as admin yet not impersonating a merchant.
     *
     * In which case safely default to the main admin view
     */
    throw redirect({ to: "/admin" });
  }

  return loggedInUserEmail;
};

/**
 * Check if the user has the role
 *
 * If the user has the VOLUME_ADMIN or VOLUME_ADMIN_READ role, they are considered to have the role
 *
 * @param role - The role to check
 * @returns true if the user has the role, false otherwise
 */
export function hasRole(
  role: RoleId,
  authorities: string[] | readonly string[],
) {
  if (
    authorities.includes("VOLUME_ADMIN") ||
    authorities.includes("VOLUME_ADMIN_READ")
  ) {
    return true;
  }

  if (authorities.includes(role)) {
    return true;
  }
  return false;
}

export const AuthorityRequired = ({
  authoritiesRequired,
  authAuthorities,
  children,
}: {
  authoritiesRequired: RoleId[];
  authAuthorities: readonly string[];
  children: React.ReactNode;
}) => {
  if (
    authAuthorities.includes("VOLUME_ADMIN") ||
    authAuthorities.includes("VOLUME_ADMIN_READ")
  ) {
    return children;
  }

  if (
    authAuthorities.some((authority) =>
      authoritiesRequired.includes(authority as RoleId),
    )
  ) {
    return children;
  }

  return null;
};

/**
 * Returns true if the given URL is a safe internal path (relative, not protocol-relative or absolute).
 * Only allows paths starting with '/'.
 */
export function isSafeInternalUrl(url: string | undefined | null): boolean {
  if (!url) return false;
  // Disallow protocol-relative (//...) and absolute (http..., https..., etc.) URLs
  // Allow only relative paths starting with '/'
  return /^\/[a-zA-Z0-9_\-/?#=&%.]*$/.test(url);
}
